import { useState, useCallback } from 'react';
import { House, Transaction } from '../../data/houses';
import { Camera } from 'lucide-react';
import axios from '../../../axios-config';
import QRScanner from '../../../components/Acorn/QRScanner';
import ScanSuccess from './ScanSuccess';

interface ScannerCardProps {
  house: House;
  onScan: () => void;
  addPoints: (houseId: string, points: number, source: 'manual' | 'coupon') => Transaction;
  updateHouse?: (id: string, data: Partial<House>) => void;
  mascotUserId?: string;
}

export default function ScannerCard({ house, onScan, addPoints, mascotUserId }: ScannerCardProps) {
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [scanResponse, setScanResponse] = useState<any>(null);

  const handleQRScanCode = async (code: string): Promise<any> => {
    try {
      // First, redeem the coupon through the API on behalf of the mascot user
      const targetUserId = mascotUserId || house.id;
      const response = await axios.post(`/api/Acorns/Redeem?acornCode=${code}&targetUserId=${targetUserId}`);
      
      // response.points is the NEW total balance, response.pointsAdded is what was just added
      const newTotal = response.data?.points;
      if (newTotal != null && Number.isFinite(newTotal)) {
        const delta = Number(newTotal) - house.points;
        if (delta !== 0) {
          addPoints(house.id, delta, 'coupon');
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error processing coupon:', error);
      return null;
    }
  };

  const handleScannerSuccess = (response?: any) => {
    setScanResponse(response);
    setShowQRScanner(false);
    setShowSuccess(true);
  };

  const handleSuccessComplete = useCallback(() => {
    setShowSuccess(false);
    setScanResponse(null);
  }, []);

  const handleScanClick = () => {
    setShowQRScanner(true);
    onScan();
  };

  return (
    <div
      className="relative backdrop-blur-xl bg-white/10 rounded-3xl p-8 shadow-2xl border-2 transition-all duration-500"
      style={{
        borderColor: house.color + '80',
        boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3), 0 0 40px ${house.color}40`,
      }}
    >
      <div className="flex flex-col items-center space-y-6">
        <div
          className="relative w-64 h-64 rounded-2xl border-4 border-dashed flex items-center justify-center cursor-pointer hover:scale-105 transition-transform duration-300"
          style={{ borderColor: house.accent }}
          onClick={handleScanClick}
        >
          <div className="absolute inset-0 bg-black/20 rounded-2xl" />
          <Camera
            size={80}
            className="relative z-10"
            style={{ color: house.accent }}
          />

          <div
            className="absolute inset-4 border-2 rounded-xl animate-pulse"
            style={{ borderColor: house.accent + '60' }}
          />
        </div>

        <div className="text-center space-y-2">
          <p className="text-white/80 text-lg font-medium">
            Scan your coupon for
          </p>
          <div className="flex items-center justify-center space-x-3">
            <span className="text-5xl">{house.icon}</span>
            <h2
              className="text-3xl font-bold tracking-wider"
              style={{ color: house.accent }}
            >
              {house.name.toUpperCase()}
            </h2>
          </div>
          <div className="flex items-center justify-center space-x-2 pt-2">
            <div
              className="w-16 h-1 rounded"
              style={{ backgroundColor: house.accent }}
            />
            <p
              className="text-4xl font-bold"
              style={{ color: house.accent }}
            >
              {house.points.toLocaleString()}
            </p>
            <div
              className="w-16 h-1 rounded"
              style={{ backgroundColor: house.accent }}
            />
          </div>
          <p className="text-white/60 text-sm">points</p>
        </div>
      </div>

      {showQRScanner && (
        <QRScanner
          onClose={() => setShowQRScanner(false)}
          onRedeemSuccess={handleScannerSuccess}
          mode="redeem"
          onCode={handleQRScanCode}
          title="Scan Coupon"
          submitLabel="Redeem"
        />
      )}

      {showSuccess && (
        <ScanSuccess
          house={house}
          response={scanResponse}
          onComplete={handleSuccessComplete}
        />
      )}
    </div>
  );
}
